/**
 * Service worker registration.
 *
 * Imported for its side effect by main.js. Every reason NOT to register is
 * checked here rather than inside public/sw.js, so the worker file stays about
 * caching policy and this file stays about who gets one.
 *
 * ── Does a release still reach users promptly? ──────────────────────────────
 * Yes, and this is worth stating because it is the usual objection to shipping
 * a worker without `skipWaiting`.
 *
 * A new worker waits for every tab to close before it activates, so the WORKER
 * can lag by a session. The APP does not: public/sw.js answers navigations
 * network-first, so a reload fetches the current index.html, which names the
 * new content-hashed bundles, which are not in the cache and are therefore
 * fetched fresh. A code fix lands on reload exactly as it does with no worker
 * installed. Only a change to the caching policy itself waits.
 *
 * That is the trade we want in a clinical app: fixes ship at the usual speed,
 * and no worker ever swaps a bundle out from under an open dose entry.
 */

import { DEMO_MODE } from '@demo/config';

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  // The public demo is a frame inside praxio.net, served from /demo-app/ with a
  // hash router. It is marketing, it must stay disposable, and a worker scoped
  // to a subdirectory of the portal would outlive the page that installed it.
  if (DEMO_MODE) return;

  // A worker in front of webpack-dev-server serves yesterday's shell against
  // today's HMR socket. Baked in at build time, so this cannot be flipped by a
  // client claiming to be localhost.
  if (process.env.NODE_ENV !== 'production') return;

  // Registration needs a secure context. Browsers already treat localhost as
  // one, so this only ever excludes a plain-http host — where the request would
  // fail anyway, noisily, in the console.
  if (!window.isSecureContext) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      // A failed registration costs a repeat download and installability, not
      // correctness — the app is fully functional without a worker, so this is
      // reported and swallowed rather than surfaced to a pharmacist.
      console.warn('[sw] registration failed:', error);
    });
  });
}
